import { FullStory, init as initializeFullStory } from '@fullstory/browser';
import { LAUNCH_DARKLY_FLAGS } from 'common/launchDarklyFlags';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { type FC, useEffect, useRef } from 'react';

type CaptureStatus = 'not-started' | 'capturing' | 'stopped';

/**
 * Renderless component that starts, stops and restarts FullStory capture from the
 * `fullstory-enabled` LaunchDarkly flag. Never runs on localhost, whatever the flag says.
 *
 * Must be placed inside the LaunchDarkly provider: the flag is only known once the LD client
 * is ready, so FullStory can't be started at module load. FullStory can only be initialised
 * once per page, so a flag flip after start uses shutdown/restart instead of re-initialising.
 */
const FullStoryInitializer: FC = () => {
  const [isFullStoryFlagEnabled] = useFeatureFlags([
    { flagKey: LAUNCH_DARKLY_FLAGS['fullstory-enabled'], defaultValue: false },
  ]);
  const isLocal = window.location.hostname === 'localhost';
  const isFullStoryEnabled = isFullStoryFlagEnabled && !isLocal;
  const captureStatus = useRef<CaptureStatus>('not-started');

  useEffect(() => {
    if (isFullStoryEnabled) {
      if (captureStatus.current === 'not-started') {
        initializeFullStory({ orgId: import.meta.env.REACT_APP_FULLSTORY_ORG_ID ?? '' });
      } else if (captureStatus.current === 'stopped') {
        FullStory('restart');
      }

      captureStatus.current = 'capturing';
      return;
    }

    if (captureStatus.current === 'capturing') {
      FullStory('shutdown');
      captureStatus.current = 'stopped';
    }
  }, [isFullStoryEnabled]);

  return null;
};

export default FullStoryInitializer;
